/**
 * useAuthenticatedRequest Hook
 *
 * A custom hook that ensures API requests are only made when:
 * 1. User is authenticated
 * 2. Auth system is fully initialized (authReady = true)
 *
 * This eliminates race conditions where components try to make API calls
 * before authentication is fully configured.
 *
 * Usage:
 * ```typescript
 * const { request, isReady } = useAuthenticatedRequest();
 *
 * useEffect(() => {
 *   if (isReady) {
 *     request(() => axios.get('/api/data/'))
 *       .then(response => setData(response.data))
 *       .catch(error => setError(error));
 *   }
 * }, [isReady]);
 * ```
 */

import React, { useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface UseAuthenticatedRequestReturn {
  /**
   * Execute an authenticated request
   * @param fn - Function that returns a Promise (typically an axios call)
   * @returns Promise that resolves when the request completes
   * @throws Error if not authenticated or auth not ready
   */
  request: <T>(fn: () => Promise<T>) => Promise<T>;

  /**
   * True when user is authenticated AND auth system is ready
   * Use this in useEffect dependencies
   */
  isReady: boolean;

  /**
   * True if user is authenticated (but auth system may not be ready yet)
   */
  isAuthenticated: boolean;

  /**
   * True if auth system is fully initialized
   */
  authReady: boolean;
}

export const useAuthenticatedRequest = (): UseAuthenticatedRequestReturn => {
  const { authReady, isAuthenticated } = useAuth();

  const request = useCallback(
    async <T,>(fn: () => Promise<T>): Promise<T> => {
      if (!isAuthenticated) {
        throw new Error('User is not authenticated. Please log in.');
      }

      if (!authReady) {
        throw new Error('Authentication system is not ready. Please wait.');
      }

      return fn();
    },
    [authReady, isAuthenticated]
  );

  return {
    request,
    isReady: isAuthenticated && authReady,
    isAuthenticated,
    authReady,
  };
};

/**
 * useAuthenticatedEffect Hook
 *
 * A specialized hook for running effects that need authenticated API calls.
 * Only runs the effect when auth is ready.
 *
 * Usage:
 * ```typescript
 * useAuthenticatedEffect(() => {
 *   axios.get('/api/data/')
 *     .then(response => setData(response.data));
 * }, [otherDep1, otherDep2]);
 * ```
 */
export const useAuthenticatedEffect = (
  effect: () => void | (() => void),
  deps: React.DependencyList = []
): void => {
  const { authReady, isAuthenticated } = useAuth();

  // Only run effect when authenticated and ready
  React.useEffect(() => {
    if (isAuthenticated && authReady) {
      return effect();
    }
  }, [isAuthenticated, authReady, ...deps]);
};
